import React, { useRef, useState } from "react";
import { Info, Lightbulb, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useAgentInstructions, NO_AGENT_INSTRUCTIONS } from "../../../hooks/queries/useAgentInstructions";
import { useSaveAgentInstructions } from "../../../hooks/mutations/useSaveAgentInstructions";
import { useAutoGrowTextarea } from "../../../hooks/useAutoGrowTextarea";
import {
  AGENT_INSTRUCTIONS_SAVED_MESSAGE,
  lastChangedLine,
} from "../../../lib/agentInstructions";
import { AGENT_INSTRUCTIONS_LIMIT, AgentInstructions } from "../../../types";
import AgentsSection from "./AgentsSection";

/**
 * The person's Agent instructions: standing rules every agent follows in
 * their JuDI, whichever assistant and whichever device.
 *
 * Agents can change this text too, so a save goes through only over the
 * version this editor loaded. When an agent saved in between, nothing is
 * overwritten: the person's unsaved text stays in the box and what is stored
 * now is shown beside it, to combine by hand.
 *
 * The rules do not belong to any one connection. With no assistant connected
 * they stay, for the next one — someone moving from one assistant to another
 * keeps them.
 */

const AgentInstructionsEditor: React.FC<{ noAssistantYet?: boolean }> = ({
  noAssistantYet = false,
}) => {
  const { data = NO_AGENT_INSTRUCTIONS, isLoading } = useAgentInstructions();
  const save = useSaveAgentInstructions();

  // null while the person has not typed: the box then follows what is stored.
  const [draft, setDraft] = useState<string | null>(null);
  // The version the person started editing from — what a save goes over.
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [conflict, setConflict] = useState<AgentInstructions | null>(null);
  // Two lines to start, so the box reads as a place for a rule or two, not
  // a paragraph. It grows with every line, so all the rules stay in view.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(textareaRef);

  const value = draft ?? data.body;
  const dirty = draft !== null && draft !== data.body;
  const lastChanged = lastChangedLine(data);
  // An agent adds and removes a rule as a line of its own, so the tip to keep
  // one rule per line shows until the first rules are saved.
  const showTip = !isLoading && !data.body.trim();

  const handleChange = (next: string) => {
    if (draft === null) setBaseVersion(data.version);
    setDraft(next);
  };

  const handleCancel = () => {
    setDraft(null);
    setBaseVersion(null);
    setConflict(null);
  };

  const handleSave = async () => {
    try {
      const result = await save.mutateAsync({
        body: value,
        expectedVersion: baseVersion ?? data.version,
      });
      if (result.saved) {
        handleCancel();
        toast.success(AGENT_INSTRUCTIONS_SAVED_MESSAGE, { duration: 6000 });
        return;
      }
      // Somebody saved first. Keep the person's text; the next save goes
      // over what is stored now, once they have combined the two.
      setConflict(result);
      setBaseVersion(result.version);
    } catch {
      // The mutation already toasts the failure.
    }
  };

  return (
    <AgentsSection
      title="Agent instructions"
      titleId="agent-instructions-title"
      descriptionId="agent-instructions-help"
      description={
        <>
          Rules every agent follows when it works in your JuDI, on any
          device. You can also ask an agent to add a rule: it shows you the
          change and saves it only after you say yes. Nobody else sees these,
          not even people you share a list with.
        </>
      }
    >
      <div className="border border-warm-border rounded-xl p-4 space-y-3">
        {noAssistantYet && (
          <div className="flex items-start gap-2 text-xs text-warm-text-secondary">
            <Info size={14} className="mt-0.5 flex-shrink-0 text-warm-blue" />
            <p>
              You can write rules before you connect an assistant. They stay
              here, and every assistant you connect later follows them.
            </p>
          </div>
        )}

        {conflict && (
          <div
            role="alert"
            className="rounded-soft border border-warm-blue bg-warm-bg-elevated p-3 space-y-2"
          >
            <p className="text-sm font-medium text-warm-text-primary">
              {conflict.changed_by
                ? `${conflict.changed_by} changed these instructions while you were editing.`
                : "These instructions changed on another device while you were editing."}
            </p>
            <p className="text-sm text-warm-text-secondary">
              Nothing was saved. Your text is still in the box. This is what is
              saved now — combine the two in the box, then save again.
            </p>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-soft bg-warm-bg-base px-3 py-2 text-xs text-warm-text-primary font-sans">
              {conflict.body || "(empty)"}
            </pre>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-warm-text-tertiary py-3">
            <Loader2 size={15} className="animate-spin" />
            Loading
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            id="agent-instructions"
            aria-labelledby="agent-instructions-title"
            aria-describedby={
              showTip
                ? "agent-instructions-help agent-instructions-tip"
                : "agent-instructions-help"
            }
            value={value}
            onChange={(event) => handleChange(event.target.value)}
            maxLength={AGENT_INSTRUCTIONS_LIMIT}
            rows={2}
            placeholder="For example: Put new tasks in the Work list."
            className="w-full resize-none overflow-hidden text-sm px-3 py-2 rounded-soft bg-warm-bg-base border border-warm-border text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:border-warm-blue transition-colors"
          />
        )}

        {showTip && (
          <div className="flex items-start gap-2 text-xs text-warm-text-secondary">
            <Lightbulb size={14} className="mt-0.5 flex-shrink-0 text-warm-blue" />
            <p id="agent-instructions-tip">
              Write each rule on its own line, not as one long paragraph.
              Separate rules are easier for an agent to follow, and easier to
              change or remove later.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="flex-1 min-w-0 text-xs text-warm-text-tertiary">
            {lastChanged ?? "No rules yet."}
            <span className="whitespace-nowrap">
              {" "}
              · {value.length.toLocaleString()} / {AGENT_INSTRUCTIONS_LIMIT.toLocaleString()}
            </span>
          </p>

          {(dirty || conflict) && (
            <button
              type="button"
              onClick={handleCancel}
              className="text-sm px-3.5 py-2 rounded-lg border border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || save.isPending}
            className="flex items-center gap-1.5 text-sm text-white bg-warm-blue hover:bg-warm-blue-dark transition-colors px-3.5 py-2 rounded-lg disabled:opacity-50 disabled:pointer-events-none"
          >
            {save.isPending && <Loader2 size={15} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </AgentsSection>
  );
};

export default AgentInstructionsEditor;
